import { DateTime } from "luxon"

import { IdCardHolderKind, IdCardType } from "./idCardsListTypes"
import type { IdCardCompany, IdCardHolder, IdCardListItem } from "./idCardsListTypes"

const BANEDANMARK: IdCardCompany = { id: "1", name: "Banedanmark" }
const DSB_VEDLIGEHOLD: IdCardCompany = { id: "2", name: "DSB Vedligehold A/S" }
const RAIL_SERVICE_NORD: IdCardCompany = { id: "3", name: "Rail Service Nord ApS" }

const AUTUMN_BATCH = "Autumn print run"
const SPRING_BATCH = "Spring run"
const PILOT_BATCH = "Pilot"

const AUTUMN_CREATED = 2
const SPRING_CREATED = 46
const PILOT_CREATED = 120

const NOW = DateTime.now()

const daysAgo = (days: number) => NOW.minus({ days }).toISO()
const dateFromToday = (days: number) => NOW.plus({ days }).toISODate()

const user = (name: string, employeeId: string): IdCardHolder => ({
    kind: IdCardHolderKind.USER,
    name,
    employeeId,
})

const guest = (company: IdCardCompany): IdCardHolder => ({
    kind: IdCardHolderKind.COMPANY,
    name: company.name,
})

// Mock cards until the ID cards API exists; dates are relative so the statuses stay stable.
export const MOCK_ID_CARDS: ReadonlyArray<IdCardListItem> = [
    {
        id: 1,
        cardNumber: "884219073355",
        cardType: IdCardType.PAYROLL,
        batch: AUTUMN_BATCH,
        created: daysAgo(AUTUMN_CREATED),
        isBlocked: false,
        scansToday: 0,
    },
    {
        id: 2,
        cardNumber: "271650098412",
        cardType: IdCardType.PAYROLL,
        batch: AUTUMN_BATCH,
        created: daysAgo(AUTUMN_CREATED),
        isBlocked: false,
        scansToday: 0,
    },
    {
        id: 3,
        cardNumber: "503188724690",
        cardType: IdCardType.GUEST,
        batch: AUTUMN_BATCH,
        created: daysAgo(AUTUMN_CREATED),
        isBlocked: false,
        scansToday: 0,
    },
    {
        id: 4,
        cardNumber: "837216054499",
        cardType: IdCardType.PAYROLL,
        batch: AUTUMN_BATCH,
        created: daysAgo(AUTUMN_CREATED),
        isBlocked: false,
        holder: user("Freja Poulsen", "10057"),
        company: BANEDANMARK,
        assignedOn: daysAgo(1),
        validFrom: dateFromToday(-1),
        scansToday: 0,
    },
    {
        id: 5,
        cardNumber: "690327415888",
        cardType: IdCardType.GUEST,
        batch: AUTUMN_BATCH,
        created: daysAgo(AUTUMN_CREATED),
        isBlocked: false,
        holder: guest(DSB_VEDLIGEHOLD),
        company: DSB_VEDLIGEHOLD,
        assignedOn: daysAgo(1),
        validFrom: dateFromToday(-1),
        validTo: dateFromToday(6),
        scansToday: 0,
        scansPerDay: 3,
    },
    {
        id: 6,
        cardNumber: "419038827506",
        cardType: IdCardType.PAYROLL,
        batch: SPRING_BATCH,
        created: daysAgo(SPRING_CREATED),
        isBlocked: false,
        holder: user("Louise Bech", "10053"),
        company: BANEDANMARK,
        assignedOn: daysAgo(40),
        validFrom: dateFromToday(-40),
        validTo: dateFromToday(9),
        scansToday: 1,
        scansPerDay: 2,
    },
    {
        id: 7,
        cardNumber: "650277311948",
        cardType: IdCardType.REFILL,
        batch: SPRING_BATCH,
        created: daysAgo(SPRING_CREATED),
        isBlocked: false,
        holder: user("Astrid Holm", "10051"),
        company: BANEDANMARK,
        assignedOn: daysAgo(39),
        validFrom: dateFromToday(-39),
        scansToday: 1,
    },
    {
        id: 8,
        cardNumber: "118460592037",
        cardType: IdCardType.GUEST,
        batch: SPRING_BATCH,
        created: daysAgo(SPRING_CREATED),
        isBlocked: false,
        holder: guest(RAIL_SERVICE_NORD),
        company: RAIL_SERVICE_NORD,
        assignedOn: daysAgo(12),
        validFrom: dateFromToday(-12),
        validTo: dateFromToday(-2),
        scansToday: 0,
        scansPerDay: 3,
    },
    {
        id: 9,
        cardNumber: "306174829551",
        cardType: IdCardType.SUBSCRIPTION,
        batch: SPRING_BATCH,
        created: daysAgo(SPRING_CREATED),
        isBlocked: false,
        holder: user("Mikkel Sørensen", "10052"),
        company: DSB_VEDLIGEHOLD,
        assignedOn: daysAgo(30),
        validFrom: dateFromToday(-30),
        validTo: dateFromToday(335),
        scansToday: 2,
    },
    {
        id: 10,
        cardNumber: "772905183346",
        cardType: IdCardType.SUBSCRIPTION,
        batch: SPRING_BATCH,
        created: daysAgo(SPRING_CREATED),
        isBlocked: false,
        holder: user("Emil Nygaard", "10056"),
        company: DSB_VEDLIGEHOLD,
        assignedOn: daysAgo(25),
        validFrom: dateFromToday(-25),
        scansToday: 0,
    },
    {
        id: 11,
        cardNumber: "145609772301",
        cardType: IdCardType.REFILL,
        batch: SPRING_BATCH,
        created: daysAgo(SPRING_CREATED),
        isBlocked: false,
        holder: user("Sofie Andersen", "10055"),
        company: RAIL_SERVICE_NORD,
        assignedOn: daysAgo(20),
        validFrom: dateFromToday(-20),
        validTo: dateFromToday(-1),
        scansToday: 0,
    },
    {
        id: 12,
        cardNumber: "944012765183",
        cardType: IdCardType.REFILL,
        batch: PILOT_BATCH,
        created: daysAgo(PILOT_CREATED),
        isBlocked: true,
        holder: user("Jeppe Fjord", "10050"),
        company: BANEDANMARK,
        assignedOn: daysAgo(118),
        validFrom: dateFromToday(-118),
        validTo: dateFromToday(200),
        scansToday: 0,
    },
    {
        id: 13,
        cardNumber: "258841690723",
        cardType: IdCardType.PAYROLL,
        batch: PILOT_BATCH,
        created: daysAgo(PILOT_CREATED),
        isBlocked: false,
        holder: user("Oliver Kjær", "10054"),
        company: BANEDANMARK,
        assignedOn: daysAgo(110),
        validFrom: dateFromToday(-110),
        scansToday: 1,
        scansPerDay: 1,
    },
    {
        id: 14,
        cardNumber: "321770946182",
        cardType: IdCardType.SUBSCRIPTION,
        batch: PILOT_BATCH,
        created: daysAgo(PILOT_CREATED),
        isBlocked: false,
        holder: user("Noah Christensen", "10058"),
        company: RAIL_SERVICE_NORD,
        assignedOn: daysAgo(90),
        validFrom: dateFromToday(-90),
        validTo: dateFromToday(30),
        scansToday: 1,
    },
]
